import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TermsAcceptance from "@/components/TermsAcceptance";
import {
  Snowflake,
  ArrowLeft,
  Gift,
  Minus,
  Plus,
  Mail,
  User,
  Heart,
  Download,
  CheckCircle2,
  Printer,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const VOUCHER_AMOUNT = 10;
const SERVICE_FEE = 2.00;

const GutscheinBooking = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [batchData, setBatchData] = useState(null); // {batch_id, vouchers:[]}
  const [completed, setCompleted] = useState(false);
  const [paidVouchers, setPaidVouchers] = useState([]);
  const [agbAccepted, setAgbAccepted] = useState(false);

  const [formData, setFormData] = useState({
    purchaserName: "",
    purchaserEmail: "",
    recipientName: "",
    quantity: 1,
  });

  const totalPrice = formData.quantity * VOUCHER_AMOUNT;
  const grandTotal = totalPrice + SERVICE_FEE;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, completed]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const adjustQuantity = (delta) => {
    setFormData((prev) => ({
      ...prev,
      quantity: Math.max(1, Math.min(20, prev.quantity + delta)),
    }));
  };

  const handleProceedToPayment = async () => {
    if (!formData.purchaserName || !formData.purchaserEmail) {
      toast.error("Bitte Name und E-Mail ausfüllen");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/gutschein/create`, {
        purchaser_name: formData.purchaserName,
        purchaser_email: formData.purchaserEmail,
        recipient_name: formData.recipientName || null,
        quantity: formData.quantity,
      });
      setBatchData(data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Erstellen der Bestellung");
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    const { data } = await axios.post(`${API}/paypal/create-order`, {
      booking_id: batchData.batch_id,
      booking_type: "gutschein",
    });
    return data.id;
  };

  const onApprove = async (data) => {
    try {
      const resp = await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID,
        booking_id: batchData.batch_id,
        booking_type: "gutschein",
      });
      setPaidVouchers(resp.data.vouchers || batchData.vouchers);
      setCompleted(true);
      toast.success("Zahlung erfolgreich! Deine Gutscheine sind bereit.");
    } catch (err) {
      toast.error("Fehler bei der Zahlungsabwicklung");
    }
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50 py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-xl" data-testid="gutschein-success-card">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2
                className="text-2xl font-semibold text-slate-900 mb-2"
                style={{ fontFamily: "Playfair Display" }}
              >
                Zahlung erfolgreich!
              </h2>
              <p className="text-slate-600 mb-6">
                Deine Gutscheine wurden an <strong>{formData.purchaserEmail}</strong> versendet.
                Zusätzlich kannst du sie hier direkt herunterladen und ausdrucken.
              </p>

              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-center gap-2 text-sky-800 mb-2">
                  <Printer className="w-4 h-4" />
                  <span className="text-sm font-semibold">So geht's weiter</span>
                </div>
                <p className="text-sm text-slate-700">
                  PDF herunterladen → auf normalem Papier ausdrucken → verschenken. Die beschenkte
                  Person zeigt den Gutschein bei ihrem Besuch an der Eisbahn vor und der QR-Code wird
                  eingelöst.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {paidVouchers.map((v) => (
                  <div
                    key={v.code}
                    className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4"
                    data-testid={`voucher-${v.code}`}
                  >
                    <div className="text-left">
                      <div className="font-mono font-semibold text-slate-900">{v.code}</div>
                      <div className="text-sm text-slate-500">
                        Eisbahn-Gutschein
                      </div>
                    </div>
                    <a
                      href={`${API}/gutschein/pdf/${v.code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        size="sm"
                        className="rounded-full bg-sky-500 hover:bg-sky-600"
                        data-testid={`download-${v.code}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </Button>
                    </a>
                  </div>
                ))}
              </div>

              <Link to="/">
                <Button
                  className="w-full h-12 rounded-full bg-sky-500 hover:bg-sky-600"
                  data-testid="back-home-btn"
                >
                  Zurück zur Startseite
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-slate-600 hover:text-sky-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </Link>
          <div className="flex items-center gap-3">
            <Snowflake className="w-6 h-6 text-sky-500" />
            <span
              className="font-semibold text-slate-900"
              style={{ fontFamily: "Playfair Display" }}
            >
              Grömitzer Eiszeit
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Intro */}
          <div className="lg:sticky lg:top-24 lg:h-fit">
            <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 rounded-full px-4 py-2 mb-4">
              <Gift className="w-4 h-4" />
              <span className="text-sm font-medium">Das perfekte Geschenk</span>
            </div>
            <h1
              className="text-4xl md:text-5xl font-medium text-slate-900 mb-4"
              style={{ fontFamily: "Playfair Display" }}
            >
              Gutschein verschenken
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Verschenke winterlichen Zauber auf der Grömitzer Eiszeit!
              <br />
              <strong>Einlösbar ausschließlich für Leistungen der Eisbahn</strong> (z. B. Eintritt,
              Schlittschuhverleih) – <em>nicht</em> für Gastronomie oder Almhütte.
            </p>

            <Card className="border-0 shadow-lg mb-6">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Printer className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Zum Ausdrucken</h4>
                    <p className="text-sm text-slate-600">
                      Du erhältst den Gutschein per E-Mail als PDF sowie als direkten Download.
                      Kein Versand nötig.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Persönlich gestaltet</h4>
                    <p className="text-sm text-slate-600">
                      Optional kann der Name der beschenkten Person auf dem Gutschein erscheinen.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Snowflake className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">3 Jahre gültig</h4>
                    <p className="text-sm text-slate-600">
                      Gutscheine sind ab Ausstellungsdatum drei Jahre gültig. Keine Barauszahlung.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Form */}
          <div>
            {step === 1 && (
              <Card className="border-0 shadow-lg" data-testid="gutschein-form-card">
                <CardHeader>
                  <CardTitle style={{ fontFamily: "Playfair Display" }}>Gutschein-Daten</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Quantity */}
                  <div>
                    <Label className="text-slate-700 mb-2 block">Anzahl Gutscheine</Label>
                    <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustQuantity(-1)}
                        disabled={formData.quantity <= 1}
                        data-testid="qty-decrease"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span
                        className="text-3xl font-bold text-slate-900"
                        data-testid="gutschein-quantity"
                      >
                        {formData.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustQuantity(1)}
                        disabled={formData.quantity >= 20}
                        data-testid="qty-increase"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Purchaser */}
                  <div>
                    <Label htmlFor="purchaserName" className="text-slate-700">
                      Dein Name (Käufer)
                    </Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="purchaserName"
                        name="purchaserName"
                        value={formData.purchaserName}
                        onChange={handleInputChange}
                        placeholder="Max Mustermann"
                        className="pl-10 h-11"
                        data-testid="input-purchaser-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="purchaserEmail" className="text-slate-700">
                      Deine E-Mail
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="purchaserEmail"
                        name="purchaserEmail"
                        type="email"
                        value={formData.purchaserEmail}
                        onChange={handleInputChange}
                        placeholder="max@mustermann.de"
                        className="pl-10 h-11"
                        data-testid="input-purchaser-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="recipientName" className="text-slate-700">
                      Für (Empfänger, optional)
                    </Label>
                    <div className="relative mt-1">
                      <Heart className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="recipientName"
                        name="recipientName"
                        value={formData.recipientName}
                        onChange={handleInputChange}
                        placeholder="Z.B. Oma Emma"
                        className="pl-10 h-11"
                        data-testid="input-recipient-name"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Wird direkt auf den Gutschein gedruckt.
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-slate-600 text-sm">
                      <span>{formData.quantity} × Gutschein</span>
                      <span>{totalPrice.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-slate-500 text-sm">
                      <span>Servicegebühr</span>
                      <span>{SERVICE_FEE.toFixed(2)} €</span>
                    </div>
                    <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                      <span className="font-semibold text-slate-900">Zu zahlen</span>
                      <span
                        className="text-2xl font-bold text-sky-600"
                        data-testid="gutschein-total"
                      >
                        {grandTotal.toFixed(2)} €
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleProceedToPayment}
                    disabled={loading}
                    className="w-full h-12 rounded-full bg-sky-500 hover:bg-sky-600"
                    data-testid="proceed-to-payment-btn"
                  >
                    {loading ? "Wird erstellt..." : "Weiter zur Zahlung"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {step === 2 && batchData && (
              <Card className="border-0 shadow-lg" data-testid="gutschein-payment-card">
                <CardHeader>
                  <CardTitle style={{ fontFamily: "Playfair Display" }}>Zahlung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="bg-slate-50 rounded-xl p-5">
                    <h4 className="font-semibold text-slate-900 mb-3">Zusammenfassung</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Käufer</span>
                        <span>{formData.purchaserName}</span>
                      </div>
                      {formData.recipientName && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Empfänger</span>
                          <span>{formData.recipientName}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Anzahl</span>
                        <span>{formData.quantity} Gutschein{formData.quantity > 1 ? "e" : ""}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Zwischensumme</span>
                        <span className="text-slate-700">{totalPrice.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Servicegebühr</span>
                        <span className="text-slate-700">{SERVICE_FEE.toFixed(2)} €</span>
                      </div>
                      <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                        <span className="font-semibold text-slate-900">Zu zahlen</span>
                        <span className="text-2xl font-bold text-sky-600">
                          {grandTotal.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-slate-700 text-sm">
                      💳 Zahlung über <strong>PayPal</strong> (PayPal, Kreditkarte, Lastschrift).
                    </p>
                  </div>

                  {/* AGB Akzeptanz */}
                  <TermsAcceptance
                    checked={agbAccepted}
                    onChange={setAgbAccepted}
                    testId="gutschein-agb"
                  />

                  {PAYPAL_CLIENT_ID && (
                    <div className={agbAccepted ? "" : "opacity-50 pointer-events-none"}>
                      <PayPalScriptProvider
                        options={{
                          clientId: PAYPAL_CLIENT_ID,
                          currency: "EUR",
                          locale: "de_DE",
                        }}
                      >
                        <PayPalButtons
                          style={{ layout: "vertical", shape: "pill", label: "pay" }}
                          disabled={!agbAccepted}
                          createOrder={createOrder}
                          onApprove={onApprove}
                          onError={(err) => {
                            console.error("PayPal Error:", err);
                            toast.error("PayPal-Fehler. Bitte versuche es erneut.");
                          }}
                        />
                      </PayPalScriptProvider>
                    </div>
                  )}

                  {!agbAccepted && (
                    <p className="text-sm text-slate-500 text-center">
                      Bitte AGB akzeptieren, um die Zahlung fortzusetzen.
                    </p>
                  )}

                  <p className="text-xs text-slate-500 text-center pt-2 border-t border-slate-100">
                    14-Tage-Widerrufsrecht: <Link to="/gutschein/widerruf" className="underline text-sky-700 hover:text-sky-900" data-testid="widerruf-link">Gutschein-Widerruf erklären</Link>
                  </p>

                  <Button
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="w-full"
                    data-testid="back-to-details-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default GutscheinBooking;
